import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AiTemplateArrayInput from './AiTemplateArrayInput'

// Data imports
import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { useGlobalStoreData } from '../../../../../store/globalStore'

const BusinessCompanyInfoSection = ({
  control,
  fieldPrefix = 'senderCompany',
  validationRules = {},
  showTitle = false,
  requiredFieldsOnly = false
}) => {
  const { t } = useTranslation()
  const currentLang = useGlobalStoreData.language()

  const getCurrentCountries = useMemo(() => {
    const countries = currentLang === 'en' ? countryEN : countryTR
    return countries.sort((a, b) => a.countryName.localeCompare(b.countryName))
  }, [currentLang])

  const defaultValidationRules = useMemo(() => ({
    required: { required: t('this_field_cannot_be_left_blank') },
    email: {
      required: t('this_field_cannot_be_left_blank'),
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: t('valid_email')
      }
    },
    keyProducts: {
      validate: (value) => {
        if (!value || value.length === 0) {
          return t('this_field_cannot_be_left_blank')
        }
        return true
      }
    }
  }), [t])

  const rules = { ...defaultValidationRules, ...validationRules }

  return (
    <>
      {showTitle && (
        <Typography variant="h6" gutterBottom color="primary">
          {t('mailTemplate.wizard.ai.businessCompanyInfo')}
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.name`}
            control={control}
            rules={rules.required}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('mailTemplate.wizard.ai.companyName')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.companyName')}
                required
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.industry`}
            control={control}
            rules={rules.required}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('mailTemplate.wizard.ai.industry')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.industry')}
                required
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.country`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error} size="small">
                <InputLabel>{t('mailTemplate.wizard.ai.country')}</InputLabel>
                <Select
                  {...field}
                  label={t('mailTemplate.wizard.ai.country')}
                  sx={{
                    height: 40
                  }}
                >
                  {getCurrentCountries.map((country) => (
                    <MenuItem key={country.countryCode} value={country.countryName}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
                {error && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{error.message}</Typography>}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.contact_person`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('mailTemplate.wizard.ai.contactPerson')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.contactPerson')}
                required
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.contact_title`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('mailTemplate.wizard.ai.contactTitle')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.contactTitle')}
                required
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.email`}
            control={control}
            rules={rules.email}
            render={({ field, fieldState: { error, isDirty } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="email"
                label={t('mailTemplate.wizard.ai.email')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.email')}
                required
                error={!!error}
                helperText={error?.message}

                disabled={!!field.value && !isDirty}
                InputProps={{
                  endAdornment: !!field.value && !isDirty && (
                    <InputAdornment position="end">
                      <Tooltip title={t('mailTemplate.wizard.ai.emailTooltip')}>
                        <IconButton size="small" edge="end">
                          <InfoOutlinedIcon fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}

                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  },
                  '& .Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary'
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={`${fieldPrefix}.key_products`}
            control={control}
            rules={rules.keyProducts}
            render={({ field: { value = [], onChange } }) => (
              <AiTemplateArrayInput
                label={t('mailTemplate.wizard.ai.keyProducts')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.keyProducts')}
                items={value}
                onAdd={(newValue) => {
                  if (newValue.trim()) {
                    onChange([...value, { value: newValue.trim() }])
                  }
                }}
                onRemove={(index) => {
                  const newValue = [...value]
                  newValue.splice(index, 1)
                  onChange(newValue)
                }}
                onEdit={(index, newValue) => {
                  const updatedValue = [...value]
                  updatedValue[index] = { ...updatedValue[index], value: newValue }
                  onChange(updatedValue)
                }}
                required
              />
            )}
          />
        </Grid>

        {!requiredFieldsOnly && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name={`${fieldPrefix}.website`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={t('mailTemplate.wizard.ai.website')}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.website')}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 40
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name={`${fieldPrefix}.phone`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={t('mailTemplate.wizard.ai.phone')}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.phone')}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 40
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.production_capacity`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label={t('mailTemplate.wizard.ai.productionCapacity')}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.productionCapacity')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.facilities`}
                control={control}
                render={({ field: { value = [], onChange } }) => (
                  <AiTemplateArrayInput
                    label={t('mailTemplate.wizard.ai.facilities')}
                    items={value}
                    onAdd={(newValue) => {
                      if (newValue.trim()) {
                        onChange([...value, { value: newValue.trim() }])
                      }
                    }}
                    onRemove={(index) => {
                      const newValue = [...value]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                    onEdit={(index, newValue) => {
                      const updatedValue = [...value]
                      updatedValue[index] = { ...updatedValue[index], value: newValue }
                      onChange(updatedValue)
                    }}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.facilities')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.certifications`}
                control={control}
                render={({ field: { value = [], onChange } }) => (
                  <AiTemplateArrayInput
                    label={t('mailTemplate.wizard.ai.certifications')}
                    items={value}
                    onAdd={(newValue) => {
                      if (newValue.trim()) {
                        onChange([...value, { value: newValue.trim() }])
                      }
                    }}
                    onRemove={(index) => {
                      const newValue = [...value]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                    onEdit={(index, newValue) => {
                      const updatedValue = [...value]
                      updatedValue[index] = { ...updatedValue[index], value: newValue }
                      onChange(updatedValue)
                    }}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.certifications')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.market_experience`}
                control={control}
                render={({ field: { value = [], onChange } }) => (
                  <AiTemplateArrayInput
                    label={t('mailTemplate.wizard.ai.marketExperience')}
                    items={value}
                    onAdd={(newValue) => {
                      if (newValue.trim()) {
                        onChange([...value, { value: newValue.trim() }])
                      }
                    }}
                    onRemove={(index) => {
                      const newValue = [...value]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                    onEdit={(index, newValue) => {
                      const updatedValue = [...value]
                      updatedValue[index] = { ...updatedValue[index], value: newValue }
                      onChange(updatedValue)
                    }}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.marketExperience')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.company_values`}
                control={control}
                render={({ field: { value = [], onChange } }) => (
                  <AiTemplateArrayInput
                    label={t('mailTemplate.wizard.ai.companyValues')}
                    items={value}
                    onAdd={(newValue) => {
                      if (newValue.trim()) {
                        onChange([...value, { value: newValue.trim() }])
                      }
                    }}
                    onRemove={(index) => {
                      const newValue = [...value]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                    onEdit={(index, newValue) => {
                      const updatedValue = [...value]
                      updatedValue[index] = { ...updatedValue[index], value: newValue }
                      onChange(updatedValue)
                    }}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.companyValues')}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default BusinessCompanyInfoSection
